import { API_BASE, AUTH_PREFIX } from './config'
import { tokenStore } from '../auth/tokenStore'
import { refreshSession } from '../auth/refresh'
import type { ProblemDetails } from './types'
import { getLocale, t } from '../i18n'

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails
  readonly url: string

  constructor(status: number, problem: ProblemDetails, url: string) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.url = url
  }
}
export class NetworkError extends Error {
  constructor(msg = t('lib.networkError')) {
    super(msg)
    this.name = 'NetworkError'
  }
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>
export function withQuery(path: string, params?: QueryParams): string {
  if (!params) return path
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
  const s = q.toString()
  return s ? `${path}?${s}` : path
}

export interface ApiInit {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /** JSON by default; a FormData body is sent as multipart and a Blob as raw bytes. */
  body?: unknown
  /** default true: attach Bearer + pre-flight refresh + 401/403 handling. false => none of those
   * (used for /auth/login and /auth/logout, which are cookie-authenticated only). */
  auth?: boolean
  retryOn401?: boolean // default true; only consulted when auth === true
  signal?: AbortSignal
  /** Parse the response body as a Blob instead of JSON (image downloads). */
  as?: 'blob'
}

async function send(path: string, init: ApiInit, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json, application/problem+json' }
  const rawBody = init.body instanceof Blob || init.body instanceof FormData ? init.body : null
  // A FormData body sets its own multipart boundary header; a Blob keeps its own type; anything else is JSON.
  if (init.body !== undefined && !(init.body instanceof FormData)) headers['Content-Type'] = init.body instanceof Blob ? init.body.type || 'application/octet-stream' : 'application/json'
  headers['Accept-Language'] = getLocale()
  if (token) headers.Authorization = `Bearer ${token}`
  if (path.startsWith(AUTH_PREFIX)) headers['X-Requested-With'] = 'XMLHttpRequest'
  try {
    return await fetch(`${API_BASE}${path}`, {
      method: init.method ?? 'GET',
      headers,
      body: init.body === undefined ? undefined : (rawBody ?? JSON.stringify(init.body)),
      credentials: path.startsWith(AUTH_PREFIX) ? 'include' : 'same-origin',
      signal: init.signal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new NetworkError()
  }
}

async function toApiError(res: Response, url: string): Promise<ApiError> {
  const ct = res.headers.get('content-type') ?? ''
  let problem: ProblemDetails = { title: res.statusText || `HTTP ${res.status}`, status: res.status }
  if (ct.includes('json')) {
    try {
      problem = { ...problem, ...(await res.json()) }
    } catch {
      /* keep default */
    }
  }
  return new ApiError(res.status, problem, url)
}

const unauthenticated = (path: string) =>
  new ApiError(401, { title: 'Unauthorized', status: 401, detail: t('lib.sessionExpired') }, path)

export async function apiFetch<T>(path: string, init: ApiInit = {}): Promise<T> {
  const auth = init.auth ?? true
  const retryOn401 = auth && (init.retryOn401 ?? true)

  if (auth && tokenStore.isExpiringSoon()) {
    const r = await refreshSession()
    if (r === 'unauthenticated') throw unauthenticated(path)
  }

  let res = await send(path, init, auth ? tokenStore.getAccessToken() : null)

  if (res.status === 401 && retryOn401) {
    const r = await refreshSession()
    if (r === 'ok') {
      res = await send(path, init, tokenStore.getAccessToken())
    } else if (r === 'network') {
      throw new NetworkError()
    } else {
      throw await toApiError(res, path) // store already cleared -> RequireAuth redirects
    }
  }

  if (res.status === 403 && auth) void refreshSession() // claims may be stale; refresh in the background, still throw below

  if (!res.ok) throw await toApiError(res, path)
  if (res.status === 204) return undefined as T
  if (init.as === 'blob') return (await res.blob()) as T
  return (await res.json()) as T
}
